import React, { useContext, useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Button, Grid, Typography, Alert, Link } from '@mui/material'
import { CartContext } from 'contexts/CartContext'
import SectionHeader from 'components/SectionHeader'
import BatchEdit from 'components/BatchEdit'
import QuoteSummary from 'pages/Quote/QuoteSummary'
import CartItem from 'components/CartItem'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { LoadingContext } from 'contexts/LoadingContext'
import { validateWoodenTableRequirements } from 'utils/validateWoodenTable'
import { useTranslation } from 'react-i18next'
import { syncProductsStockBySkus } from 'api/products'
import { useBatchColorEdit } from 'hooks/useBatchColorEdit'

const QuotePage = () => {
  const { t } = useTranslation()
  const { quoteId } = useParams()
  const navigate = useNavigate()
  const { showLoading, stopLoading } = useContext(LoadingContext)

  const {
    quotes,
    deleteQuoteItem,
    downloadQuotePdf,
    clearQuote,
    updateQuoteItem,
    switchToCart,
    getQuoteDetails
  } = useContext(CartContext)

  const quote = useMemo(
    () => quotes.find(q => q.id === quoteId),
    [quotes, quoteId]
  )

  const {
    isBatchEditing,
    selectedItems,
    notFoundItems,
    outOfStockItems,
    isAllSelected,
    selectItem,
    toggleSelectAll,
    openBatchEdit,
    closeBatchEdit,
    runBatchUpdate
  } = useBatchColorEdit(quote?.items ?? [], quote?.id ?? '', true)
  const [stocks, setStocks] = useState<{ [sku: string]: number }>({})

  useEffect(() => {
    if (!quote?.items?.length) return
    const fetchStocks = async () => {
      try {
        const products = await syncProductsStockBySkus(
          quote.items.map(item => item.sku)
        )
        const stockMap = Object.fromEntries(
          products.map(p => [p.sku, Number(p.stock) || 0])
        )
        setStocks(stockMap)
      } catch {
        setStocks(Object.fromEntries(quote.items.map(item => [item.sku, 0])))
      }
    }
    fetchStocks()
  }, [quote?.items])

  if (!quote) {
    return (
      <FixedWidthContainer sx={{ my: '50px' }}>
        <SectionHeader title={t('quote.title')} />
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography variant='h4' color='text.secondary' gutterBottom>
            {t('quote.notFound')}
          </Typography>
          <Button
            variant='outlined'
            onClick={() => navigate('/quotes')}
            sx={{ mt: 2, minWidth: 140 }}
          >
            {t('quote.back')}
          </Button>
        </Box>
      </FixedWidthContainer>
    )
  }

  const handleUpdateQuoteItem = async ({
    cartItemId,
    quantity,
    isAssembled,
    hingeDirection,
    skuPrefix,
    poNumber
  }: {
    cartItemId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string
    skuPrefix?: string
    poNumber?: string
  }) => {
    if (cartItemId) {
      const currentItem = quote.items.find(
        item => item.cartItemId === cartItemId
      )
      if (!currentItem) {
        console.error(`Item with cartItemId ${cartItemId} not found`)
        return
      }

      const updatedHingeDirection =
        currentItem.isHingeable && isAssembled
          ? hingeDirection || 'LEFT'
          : hingeDirection || ''

      updateQuoteItem({
        quoteId: quote.id,
        cartItemId,
        quantity,
        isAssembled,
        hingeDirection: updatedHingeDirection,
        skuPrefix,
        poNumber
      })
    } else {
      updateQuoteItem({ quoteId: quote.id, poNumber })
    }
  }

  const handleDownloadPdf = async () => {
    showLoading()
    await downloadQuotePdf(quote.id)
    stopLoading()
  }

  const handleSwitchToCart = () => {
    switchToCart(quote.id)
    navigate('/cart')
  }

  const currentQuoteDetails = getQuoteDetails(quote)
  const woodenTableValidation = validateWoodenTableRequirements(
    quote?.items || []
  )

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader
        title={`QUOTE - ${quote.poNumber || 'Untitled PO Number'}`}
      />
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={8}>
          <Box mb={2} display='flex' gap={2} justifyContent='flex-end'>
            {isBatchEditing && (
              <Button
                variant={
                  isAllSelected
                    ? 'contained'
                    : ('outlined' as 'contained' | 'outlined')
                }
                sx={{
                  width: '150px',
                  height: '32px',
                  gap: '10px',
                  borderRadius: '4px',
                  borderColor: 'primary.main',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '16.94px',
                  textAlign: 'left'
                }}
                onClick={toggleSelectAll}
              >
                {t('batchEdit.selectAll')}
              </Button>
            )}

            <Button
              variant={
                isBatchEditing
                  ? 'contained'
                  : ('outlined' as 'contained' | 'outlined')
              }
              sx={{
                width: '150px',
                height: '32px',
                gap: '10px',
                borderRadius: '4px',
                borderColor: 'primary.main',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16.94px',
                textAlign: 'left'
              }}
              onClick={() =>
                isBatchEditing ? closeBatchEdit() : openBatchEdit()
              }
            >
              {t('quote.batchEdit')}
            </Button>

            <Button
              sx={{
                width: '150px',
                height: '32px',
                gap: '10px',
                borderRadius: '4px',
                color: 'primary.main',
                border: '1px solid',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16.94px',
                textAlign: 'left'
              }}
              onClick={handleDownloadPdf}
            >
              {t('quote.downloadPdf')}
            </Button>

            <Button
              sx={{
                width: '150px',
                height: '32px',
                gap: '10px',
                borderRadius: '4px',
                color: 'primary.main',
                border: '1px solid',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16.94px',
                textAlign: 'left'
              }}
              onClick={() => {
                clearQuote(quote.id)
                navigate('/quotes')
              }}
            >
              {t('quote.deleteThisCart')}
            </Button>
          </Box>

          {isBatchEditing && (
            <BatchEdit
              selectedItems={selectedItems}
              notFoundItems={notFoundItems}
              outOfStockItems={outOfStockItems}
              onBatchUpdate={runBatchUpdate}
              onCancel={closeBatchEdit}
            />
          )}

          {quote.items.map(item => (
            <CartItem
              key={item.cartItemId}
              item={item}
              stock={stocks[item.sku]}
              isSelected={selectedItems.includes(item.cartItemId)}
              isBatchEditActive={isBatchEditing}
              onSelect={isSelected => selectItem(item.cartItemId, isSelected)}
              onUpdate={updateParams =>
                handleUpdateQuoteItem({
                  cartItemId: item.cartItemId,
                  ...updateParams
                })
              }
              onDelete={() => {
                if (quote.items.length === 1) {
                  deleteQuoteItem(quote.id, item.cartItemId)
                  navigate('/quotes')
                } else {
                  deleteQuoteItem(quote.id, item.cartItemId)
                }
              }}
            />
          ))}

          {woodenTableValidation.warning && (
            <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
              {woodenTableValidation.messageParts ? (
                <>
                  {woodenTableValidation.messageParts.beforeLink}
                  <Link
                    href='/search?keyword=-ber'
                    sx={{
                      color: 'error.main',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {woodenTableValidation.messageParts.linkText}
                  </Link>
                  {woodenTableValidation.messageParts.afterLink}
                </>
              ) : (
                woodenTableValidation.warning
              )}
            </Alert>
          )}

          <Button
            variant='outlined'
            sx={{ width: '100%' }}
            onClick={() => navigate('/quotes')}
          >
            {t('quote.back')}
          </Button>
        </Grid>

        <Grid item xs={4} container justifyContent='flex-end'>
          <QuoteSummary
            quoteId={quote.id}
            currentQuoteDetails={currentQuoteDetails}
            poNumber={quote.poNumber || ''}
            onUpdatePoNumber={(newPoNumber: string) =>
              handleUpdateQuoteItem({ poNumber: newPoNumber })
            }
            onSwitchToCart={handleSwitchToCart}
          />
        </Grid>
      </Grid>
    </FixedWidthContainer>
  )
}

export default QuotePage
